import { useMemo } from 'react';

import { Flex, Link, Popover, PopoverTrigger, rem, StyleProps } from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';

import { Route } from '../../config/router';
import { UserBonusFeedProps } from '../../types/user';
import { pathWithId } from '../../utils/api';
import { splitOverflow } from '../../utils/array';
import { pluck } from '../../utils/object';
import { UserAvatar } from './UserAvatar';
import { UserAvatarExcess } from './UserAvatarExcess';
import { UserPopover } from './UserPopover';

export const DEFAULT_MAX_USERS = 5;

interface UserAvatarGroupProps extends StyleProps {
	onAvatarClick?: VoidFunction;
	isPopoverDisabled?: boolean;
	linkToUsers?: boolean;
	max?: number;
	size?: 'lg' | 'xl';
	users: UserBonusFeedProps[];
	variant?: 'compact' | 'inline';
}

interface GenerateProps extends Pick<UserAvatarGroupProps, 'size' | 'users' | 'variant'> {
	i: number;
}

export const sizeMap = {
	'2xs': 24,
	xs: 32,
	sm: 32,
	md: 44,
	lg: 52,
	xl: 64,
	'2xl': 72,
};

const avatarProps = ({ i, size = 'lg', variant, users }: GenerateProps) => {
	if (variant === 'compact' && users.length > 1) {
		return {
			marginEnd: rem(-16),
			mt: rem(12 * i),
			size: 'sm' as any,
		};
	}

	return {
		marginEnd: rem((sizeMap[size] * -1) / 2.75),
		size,
	};
};

export const UserAvatarGroup: React.FC<UserAvatarGroupProps> = ({
	isPopoverDisabled,
	linkToUsers,
	max = DEFAULT_MAX_USERS,
	onAvatarClick,
	size,
	users,
	variant = 'inline',
	...rest
}) => {
	const recognizeURL = useBuildRecognizeURL();
	const isCompact = variant === 'compact';

	const { head, tail, doesOverflow, overflowCount } = splitOverflow(users, {
		max: isCompact ? 1 : max,
		minOverflow: 2,
	});

	const overflowUserNames = useMemo(() => tail.map(pluck('display_name')), [tail]);

	return (
		<Flex dir="row" {...rest}>
			{head.map((user, i) => {
				const avatar = <UserAvatar border="2px solid white" user={user} onClick={onAvatarClick} {...avatarProps({ i, size, users, variant })} />;

				return (
					<Popover key={user.id} trigger="hover" isOpen={isPopoverDisabled ? false : undefined}>
						<PopoverTrigger>
							{linkToUsers ? (
								<Link _hover={{ textDecoration: 'none' }} href={recognizeURL(pathWithId(Route.UserProfile, user.id))}>
									{avatar}
								</Link>
							) : (
								avatar
							)}
						</PopoverTrigger>
						<UserPopover user={user} />
					</Popover>
				);
			})}

			{doesOverflow && <UserAvatarExcess count={overflowCount} userNames={overflowUserNames} {...avatarProps({ i: 1, size, users, variant })} />}
		</Flex>
	);
};
